import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP_STORE_CONFIG_TOKEN, ApplicationPathConstants, MODELS_STORE_NAME_TOKEN } from '@ddt-web/config';
import { ConfigService, InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { PortfolioStoreProxyService } from '@ddt-web/data-layer-components';
import { ApiStoreConfiguration, PositionDetails } from '@ddt-web/data-service';
import { AdditionalAnalysisParams } from '@ddt-web/domain';
import { ErrorObject, PasErrorPortfolioDetails, PasSecurityDetailsPartialError } from '@ddt-web/error-handler';
import { State, StoreService } from '@ddt-web/store-service';
import { UrlService } from '@ddt-web/url';
import { WealthWebApiService } from '@ddt-web/wealth-web';
import { UntilDestroy } from '@ngneat/until-destroy';
import * as castArray from 'lodash/castArray';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, of } from 'rxjs';
import { first, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';
import { PasDataLoaderService, PasPositionDetailsLoadedProcessingService, PasProxyProcessingService } from '../pas-setup-data-workers';
import { PortfolioResolverUtil } from '../utils/portfolio-resolver.util';

@Injectable()
@UntilDestroy()
export class NileDataLoadingWorker extends AbstractWorker {
	@InjectConfig(SharedConfigKey.IDENTIFIER_TO_BE_USED_AS_WWE_API_PARAM_ID) protected readonly identifierToBeUsedAsWweApiParamId;
	@InjectConfig(SharedConfigKey.FETCH_TAX_DETAILS) protected readonly fetchTaxDetails: boolean = false;
	@InjectConfig(SharedConfigKey.SHOULD_THROW_SECURITY_DETAILS_PARTIAL_ERROR) protected readonly throwSecurityDetailsPartialPortfoliosErrors = true;

	constructor(
		public storeService: StoreService,
		protected portfolioStoreProxyService: PortfolioStoreProxyService,
		protected dataLoaderService: PasDataLoaderService,
		protected urlService: UrlService,
		protected configService: ConfigService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig,
		@Inject(MODELS_STORE_NAME_TOKEN) public modelsStoreName,
		protected router: Router,
		protected pasPositionDetailsLoadedProcessingService: PasPositionDetailsLoadedProcessingService,
		protected pasProxyProcessingService: PasProxyProcessingService
	) {
		super();
	}

	public handle(queryParams: any): void {
		// Analysis should not fire on PE
		if (queryParams.forceAnalysis || !this.router.url.includes(ApplicationPathConstants.PORTFOLIO_EDITOR_PATH)) {

			let ids = castArray(get(queryParams, 'portfolios', []));
			const internalPortfolios = PortfolioResolverUtil.extractInternalPortfolioIds(ids);

			if (!isEmpty(ids)) {
				combineLatest([
					this.storeService.selectAll(this.storeConfig.modelsConfig),
					this.storeService.selectByIds(this.storeConfig.associatedPortfolios, ids),
					this.storeConfig.modelPortfolios ? this.storeService.selectByIds(this.storeConfig.modelPortfolios, ids) : of({} as any) // TODO sachin: we should not need this store in monitoring/ nile
				]).pipe(
					withLatestFrom(this.storeService.selectByIds(this.storeConfig.userPortfolios, ids)),
					first(([[modelsConfig, associatedPortfoliosResult, modelPortfolios], userPortfolios]) => {
						if (this.storeConfig.modelPortfolios && this.storeConfig.modelsConfig) {
							const isInternalPortfoliosSuccessfullyLoaded =
								PortfolioResolverUtil.isSavedModelPortfoliosSuccessfullyLoaded(internalPortfolios, associatedPortfoliosResult);
							return !associatedPortfoliosResult.isLoading && !userPortfolios.isLoading && !modelPortfolios.isLoading && !modelsConfig.isLoading && isInternalPortfoliosSuccessfullyLoaded;
						} else {
							const isInternalPortfoliosSuccessfullyLoaded =
								PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(internalPortfolios, null, associatedPortfoliosResult);
							return !associatedPortfoliosResult.isLoading && !userPortfolios.isLoading && isInternalPortfoliosSuccessfullyLoaded;
						}
					}),
					mergeMap(([[modelsConfig, associatedPortfoliosResult], userPortfolios]) => {
						let updateAssociatedPortfolioResult: State<any> = associatedPortfoliosResult;
						if (this.throwSecurityDetailsPartialPortfoliosErrors) {
							this.checkForFlaggedUserPortfolios(userPortfolios);
							const validPortfoliosArray = Object.values(associatedPortfoliosResult.entities).filter(e => !e['children'].some(c => c.error));
							const validPortfolios = validPortfoliosArray.reduce((o, port) => Object.assign(o, { [port['id']]: port }), {});
							ids = ids.filter((id) => validPortfoliosArray.some((port) => port['id'] === id));
							updateAssociatedPortfolioResult = {
								...associatedPortfoliosResult,
								entities: validPortfolios,
								ids: ids
							};
						}
						// set loading state of the store
						this.storeService.fetch(this.storeConfig.positionDetails);
						if (this.fetchTaxDetails) {
							this.dataLoaderService.populateStoresForPositions(
								ids,
								this.throwSecurityDetailsPartialPortfoliosErrors,
								this.identifierToBeUsedAsWweApiParamId ?
									{ identifierToBeUsedAsWweApiParamId: this.identifierToBeUsedAsWweApiParamId } as any
									: null,
								null,
								[this.pasProxyProcessingService],
								null,
								PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS_TAX
							);
						}
						return this.dataLoaderService.getPositionDetails(
							ids,
							this.throwSecurityDetailsPartialPortfoliosErrors,
							this.identifierToBeUsedAsWweApiParamId ?
								{ identifierToBeUsedAsWweApiParamId: this.identifierToBeUsedAsWweApiParamId } as any
								: null
						).pipe(map(res => [modelsConfig, updateAssociatedPortfolioResult, res]));
					})
				).subscribe(([modelsConfig, associatedPortfoliosResult, positionData]) => {
					this.postProcessingAndRequestPortfolioDetails(ids, queryParams, associatedPortfoliosResult, modelsConfig, positionData);
				});
			}
		}
	}

	protected requestPortfolioDetails(ids, config, associatedPortfolios, additionalAnalysisParams: AdditionalAnalysisParams, customConfigArray?: ApiStoreConfiguration[]): void {
		this.dataLoaderService.populateStoresForPortfolios(ids, config, associatedPortfolios, additionalAnalysisParams, null, customConfigArray);
	}

	protected checkForFlaggedUserPortfolios(userPortfolios: State<unknown>): void {
		const flaggedUserPortfolios = Object.values(userPortfolios.entities).filter(portfolio => portfolio?.['hasHoldingsError']);
		if (flaggedUserPortfolios.length) {
			throw new ErrorObject(new PasSecurityDetailsPartialError(
				null,
				flaggedUserPortfolios.map(portfolio => <PasErrorPortfolioDetails>{ id: portfolio['id'], description: portfolio['description'] })
			), WealthWebApiService.POSITION_DETAILS_CONFIG_NAME);
		}
	}

	protected postProcessingAndRequestPortfolioDetails(ids, queryParams, associatedPortfoliosResult, modelsConfig, positionData): void {
		this.dataLoaderService.positionsPostProcessing(
			positionData[0],
			positionData[1],
			associatedPortfoliosResult.ids,
			[this.pasProxyProcessingService, this.pasPositionDetailsLoadedProcessingService]
		);

		this.requestPortfolioDetails(ids, null, associatedPortfoliosResult, queryParams.additionalAnalysisParams, queryParams.customConfigArray); // nile does not have models
	}


}
